import React, { useState, useCallback, useMemo } from 'react';
import {
  WizardCard,
  WizardHeader,
  ProgressBar,
  ProgressBarFill,
  ProgressText,
  QuestionContainer,
  QuestionNumber,
  QuestionText,
  OptionsContainer,
  OptionItem,
  OptionCheckbox,
  OptionLabel,
  CustomInputContainer,
  CustomInput,
  NavigationContainer,
  NavigationButton,
  ReviewContainer,
  ReviewQuestionItem,
  ReviewQuestionTitle,
  ReviewAnswersList,
  ReviewAnswer,
  ReviewEditButton,
  SubmittedBadge
} from './WizardMultiSelection.styled';

/**
 * Wizard-style multi-selection component
 * Shows one question at a time with navigation
 * Supports review mode and read-only submitted view
 * 
 * States:
 * - answering: User going through questions one by one
 * - reviewing: User reviewing all answers before final send
 * - submitted: Read-only view of submitted answers
 */
const WizardMultiSelection = ({ 
  questions,
  onConfirm,
  isSubmitted = false, // NEW: For displaying submitted answers in read-only mode
  submittedAnswers = {}, // NEW: The actual submitted answers to display
  submittedCustomAnswers = {} // NEW: Custom answers that were submitted
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [mode, setMode] = useState(isSubmitted ? 'submitted' : 'answering'); // answering | reviewing | submitted
  const [answers, setAnswers] = useState(submittedAnswers); // Initialize with submitted answers if provided
  const [customAnswers, setCustomAnswers] = useState(submittedCustomAnswers); // Initialize with submitted custom answers
  const [isSubmittedCollapsed, setIsSubmittedCollapsed] = useState(true); // For collapsing submitted view

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentQuestionIndex];
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

  // Calculate progress
  const progress = useMemo(() => {
    const answeredCount = questions.filter((_, idx) => {
      const hasSelections = answers[idx] && answers[idx].length > 0;
      const hasCustom = customAnswers[idx]?.showInput && customAnswers[idx]?.answer.trim();
      return hasSelections || hasCustom;
    }).length;
    return {
      answered: answeredCount,
      total: totalQuestions,
      percentage: (answeredCount / totalQuestions) * 100
    };
  }, [answers, customAnswers, questions, totalQuestions]);

  // Handle option selection
  const handleOptionToggle = useCallback((option) => {
    // Check if this is the "Other" option
    if (option.id === 'custom-other') {
      setCustomAnswers(prev => {
        const current = prev[currentQuestionIndex] || { showInput: false, answer: '' };
        const newShowInput = !current.showInput;
        
        // If hiding input, clear it
        if (!newShowInput) {
          return {
            ...prev,
            [currentQuestionIndex]: { showInput: false, answer: '' }
          };
        }
        
        return {
          ...prev,
          [currentQuestionIndex]: { ...current, showInput: newShowInput }
        };
      });
      return;
    }

    setAnswers(prev => {
      const currentSelections = prev[currentQuestionIndex] || [];
      const isSelected = currentSelections.some(opt => opt.id === option.id);
      
      if (isSelected) {
        return {
          ...prev,
          [currentQuestionIndex]: currentSelections.filter(opt => opt.id !== option.id)
        };
      } else {
        return {
          ...prev,
          [currentQuestionIndex]: [...currentSelections, option]
        };
      }
    });
  }, [currentQuestionIndex]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (!isLastQuestion) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  }, [isLastQuestion]);

  const handlePrevious = useCallback(() => {
    if (!isFirstQuestion) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  }, [isFirstQuestion]);

  const handleSubmitForReview = useCallback(() => {
    setMode('reviewing');
    setCurrentQuestionIndex(0); // Reset to first question for review
  }, []);

  const handleBackToAnswering = useCallback(() => {
    setMode('answering');
  }, []);

  const handleFinalSend = useCallback(() => {
    // Format all answers
    const formattedAnswers = questions.map((q, idx) => {
      let selectedOptions = [...(answers[idx] || [])];
      
      // Add custom answer if provided
      const customData = customAnswers[idx];
      if (customData?.showInput && customData?.answer.trim()) {
        selectedOptions.push({
          id: 'custom-other',
          label: `Other: ${customData.answer}`
        });
      }
      
      const optionsList = selectedOptions.map(opt => `   ${opt.label}`).join('\n');
      return `${idx + 1}. ${q.question}\n\nSelected:\n${optionsList || '   (No options selected)'}`;
    }).join('\n\n');

    // Pass both formatted text and raw answer data
    const answersData = {
      answers: answers,
      customAnswers: customAnswers
    };
    
    onConfirm?.(formattedAnswers, answersData);
    setMode('submitted');
  }, [answers, customAnswers, questions, onConfirm]);

  const handleEditQuestion = useCallback((questionIndex) => {
    setCurrentQuestionIndex(questionIndex);
    setMode('answering');
  }, []);

  // Check if option is selected
  const isOptionSelected = useCallback((option) => {
    if (option.id === 'custom-other') {
      return customAnswers[currentQuestionIndex]?.showInput || false;
    }
    const currentSelections = answers[currentQuestionIndex] || [];
    return currentSelections.some(opt => opt.id === option.id);
  }, [answers, customAnswers, currentQuestionIndex]);

  // Get formatted answers for display
  const getFormattedAnswers = useCallback((questionIndex) => {
    let selectedOptions = [...(answers[questionIndex] || [])];
    
    const customData = customAnswers[questionIndex];
    if (customData?.showInput && customData?.answer.trim()) {
      selectedOptions.push({
        id: 'custom-other',
        label: `Other: ${customData.answer}`
      });
    }
    
    return selectedOptions;
  }, [answers, customAnswers]);

  // Render answering mode
  const renderAnsweringMode = () => (
    <>
      <QuestionContainer>
        <QuestionNumber>Question {currentQuestionIndex + 1} of {totalQuestions}</QuestionNumber>
        <QuestionText>{currentQuestion.question}</QuestionText>
        
        <OptionsContainer>
          {currentQuestion.options.map((option) => (
            <OptionItem
              key={option.id}
              $isSelected={isOptionSelected(option)}
              onClick={() => handleOptionToggle(option)}
            >
              <OptionCheckbox $isSelected={isOptionSelected(option)}>
                {isOptionSelected(option) && (
                  <svg 
                    width="12" 
                    height="10" 
                    viewBox="0 0 12 10" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path 
                      d="M1 5L4.5 8.5L11 1.5" 
                      stroke="white" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </OptionCheckbox>
              <OptionLabel>{option.label}</OptionLabel>
            </OptionItem>
          ))}
          
          {/* "Other" option */}
          <OptionItem
            $isSelected={customAnswers[currentQuestionIndex]?.showInput || false}
            onClick={() => handleOptionToggle({ id: 'custom-other' })}
          >
            <OptionCheckbox $isSelected={customAnswers[currentQuestionIndex]?.showInput || false}>
              {customAnswers[currentQuestionIndex]?.showInput && (
                <svg 
                  width="12" 
                  height="10" 
                  viewBox="0 0 12 10" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M1 5L4.5 8.5L11 1.5" 
                    stroke="white" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </OptionCheckbox>
            <OptionLabel>Other (type your own)</OptionLabel>
          </OptionItem>
          
          {customAnswers[currentQuestionIndex]?.showInput && (
            <CustomInputContainer>
              <CustomInput
                type="text"
                value={customAnswers[currentQuestionIndex]?.answer || ''}
                onChange={(e) => {
                  setCustomAnswers(prev => ({
                    ...prev,
                    [currentQuestionIndex]: {
                      ...prev[currentQuestionIndex],
                      answer: e.target.value
                    }
                  }));
                }}
                placeholder="Type your answer here..."
                onClick={(e) => e.stopPropagation()}
              />
            </CustomInputContainer>
          )}
        </OptionsContainer>
      </QuestionContainer>

      <NavigationContainer>
        <NavigationButton 
          onClick={handlePrevious}
          disabled={isFirstQuestion}
          $variant="secondary"
        >
          ← Previous
        </NavigationButton>
        
        {isLastQuestion ? (
          <NavigationButton 
            onClick={handleSubmitForReview}
            $variant="primary"
          >
            Review Answers →
          </NavigationButton>
        ) : (
          <NavigationButton 
            onClick={handleNext}
            $variant="primary"
          >
            Next →
          </NavigationButton>
        )}
      </NavigationContainer>
    </>
  );

  // Render reviewing mode
  const renderReviewingMode = () => (
    <>
      <QuestionContainer>
        <QuestionNumber>Review Your Answers</QuestionNumber>
        <QuestionText style={{ marginBottom: '20px' }}>
          Review and edit your answers before submitting
        </QuestionText>
        
        <ReviewContainer>
          {questions.map((q, idx) => {
            const formattedAnswers = getFormattedAnswers(idx);
            const isAnswered = formattedAnswers.length > 0;
            
            return (
              <ReviewQuestionItem key={idx} $isAnswered={isAnswered}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <ReviewQuestionTitle>
                    {idx + 1}. {q.question}
                  </ReviewQuestionTitle>
                  <ReviewEditButton onClick={() => handleEditQuestion(idx)}>
                    Edit
                  </ReviewEditButton>
                </div>
                
                <ReviewAnswersList>
                  {formattedAnswers.length > 0 ? (
                    formattedAnswers.map((opt, optIdx) => (
                      <ReviewAnswer key={optIdx}>• {opt.label}</ReviewAnswer>
                    ))
                  ) : (
                    <ReviewAnswer $isEmpty>No answer selected</ReviewAnswer>
                  )}
                </ReviewAnswersList>
              </ReviewQuestionItem>
            );
          })}
        </ReviewContainer>
      </QuestionContainer>

      <NavigationContainer>
        <NavigationButton 
          onClick={handleBackToAnswering}
          $variant="secondary"
        >
          ← Back to Questions
        </NavigationButton>
        
        <NavigationButton 
          onClick={handleFinalSend}
          $variant="primary"
        >
          Send Answers
        </NavigationButton>
      </NavigationContainer>
    </>
  );

  // Render submitted mode (read-only, collapsible)
  const renderSubmittedMode = () => {
    const totalAnswered = progress.answered;
    
    return (
      <>
        <QuestionContainer style={{ padding: isSubmittedCollapsed ? '16px 20px' : '24px' }}>
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              cursor: 'pointer',
              marginBottom: isSubmittedCollapsed ? '0' : '16px'
            }}
            onClick={() => setIsSubmittedCollapsed(!isSubmittedCollapsed)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <QuestionNumber>Your Submitted Answers</QuestionNumber>
              <SubmittedBadge>Sent</SubmittedBadge>
            </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              fontSize: '13px',
              color: '#6B7280'
            }}>
              <span>{totalAnswered} of {totalQuestions} answered</span>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none"
                style={{ 
                  transform: isSubmittedCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.2s ease'
                }}
              >
                <path 
                  d="M4 6L8 10L12 6" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          
          {!isSubmittedCollapsed && (
            <ReviewContainer>
              {questions.map((q, idx) => {
                const formattedAnswers = getFormattedAnswers(idx);
                
                return (
                  <ReviewQuestionItem key={idx} $isAnswered={true} $isReadOnly>
                    <ReviewQuestionTitle>
                      {idx + 1}. {q.question}
                    </ReviewQuestionTitle>
                    
                    <ReviewAnswersList>
                      {formattedAnswers.length > 0 ? (
                        formattedAnswers.map((opt, optIdx) => (
                          <ReviewAnswer key={optIdx}>• {opt.label}</ReviewAnswer>
                        ))
                      ) : (
                        <ReviewAnswer $isEmpty>No answer selected</ReviewAnswer>
                      )}
                    </ReviewAnswersList>
                  </ReviewQuestionItem>
                );
              })}
            </ReviewContainer>
          )}
        </QuestionContainer>
      </>
    );
  };

  return (
    <WizardCard $mode={mode}>
      {/* Progress Bar - Always visible except in submitted mode */}
      {mode !== 'submitted' && (
        <WizardHeader>
          <ProgressBar>
            <ProgressBarFill $percentage={progress.percentage} />
          </ProgressBar>
          <ProgressText>
            {progress.answered} of {progress.total} questions answered
          </ProgressText>
        </WizardHeader>
      )}

      {/* Content based on mode */}
      {mode === 'answering' && renderAnsweringMode()}
      {mode === 'reviewing' && renderReviewingMode()}
      {mode === 'submitted' && renderSubmittedMode()}
    </WizardCard>
  );
};

export default WizardMultiSelection;

